import styles from "./task-title-template-card.module.css";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  App,
  Button,
  Card,
  Dropdown,
  Empty,
  Flex,
  Grid,
  Input,
  Modal,
  Popover,
  Select,
  Space,
  Tag,
  Typography,
} from "antd";
import CloseIcon from "@/icons/CloseIcon";
import PlusIcon from "@/icons/PlusIcon";
import EditIcon from "@/icons/EditIcon";

import { updateField, updateTaskTitleTemplate } from "@/api/requests";

const DUE_DATE_FIELD_TYPES = new Set(["date", "moment"]);

const SYSTEM_FIELDS = [
  { key: "contact_name", label: "Ваши ФИО" },
  { key: "contact_email", label: "Email" },
  { key: "contact_phone", label: "Телефон" },
  { key: "form_name", label: "Название формы" },
];

const DEFAULT_TEMPLATE_TEXT = "Заявка - [Название формы] - [Email]";
const DEFAULT_TEMPLATE_VALUE = "Заявка - {sys:form_name} - {sys:contact_email}";

const createTextPart = (value = "") => ({
  type: "text",
  value,
});

const parseTemplate = (template, fieldsMap, systemFieldsMap) => {
  if (!template) return [createTextPart("")];

  const parts = [];
  const regex = /\{(f|sys):([^{}]+)\}/g;

  let lastIndex = 0;
  let match;

  while ((match = regex.exec(template)) !== null) {
    const [raw, source, key] = match;

    if (match.index > lastIndex) {
      parts.push(createTextPart(template.slice(lastIndex, match.index)));
    }

    if (source === "f") {
      parts.push({
        type: "field",
        fieldId: key,
        label: fieldsMap.get(key)?.label || fieldsMap.get(key)?.name || key,
      });
    }

    if (source === "sys") {
      parts.push({
        type: "system",
        key,
        label: systemFieldsMap.get(key)?.label || key,
      });
    }

    lastIndex = match.index + raw.length;
  }

  if (lastIndex < template.length) {
    parts.push(createTextPart(template.slice(lastIndex)));
  }

  return parts.length ? parts : [createTextPart("")];
};

const serializeTemplate = (parts) =>
  parts
    .map((part) => {
      if (part.type === "text") return part.value || "";
      if (part.type === "field") return `{f:${part.fieldId}}`;
      if (part.type === "system") return `{sys:${part.key}}`;
      return "";
    })
    .join("");

const getReadableTemplate = (parts) =>
  parts
    .map((part) => {
      if (part.type === "text") return part.value || "";
      return `[${part.label}]`;
    })
    .join("");

const TaskTitleTemplateCard = ({
  data,
  groups = [],
  isDeleted,
  loading,
  setPending,
  fetchData,
}) => {
  const { message } = App.useApp();

  const [parts, setParts] = useState([createTextPart("")]);
  const [dueDateFieldUuid, setDueDateFieldUuid] = useState(null);
  const [isEdit, setIsEdit] = useState(false);

  const [fieldToAdd, setFieldToAdd] = useState(null);
  const [systemFieldToAdd, setSystemFieldToAdd] = useState(null);

  const canEdit = Boolean(data?.uuid) && !data?.deleted && !isDeleted;

  const { useBreakpoint } = Grid;

  const screens = useBreakpoint();
  const isMobile = !screens.sm;

  const [fieldPopoverOpen, setFieldPopoverOpen] = useState(false);
  const [systemPopoverOpen, setSystemPopoverOpen] = useState(false);
  const [fieldModalOpen, setFieldModalOpen] = useState(false);
  const [systemModalOpen, setSystemModalOpen] = useState(false);

  const fields = useMemo(
    () =>
      groups.flatMap((group) =>
        (group.fields || []).map((field) => ({
          value: field.uuid,
          label: field.name,
          type: field.type,
          isTaskDueDate: Boolean(field.is_task_due_date),
          groupName: group.name,
        })),
      ),
    [groups],
  );

  const dueDateFields = useMemo(
    () => fields.filter((field) => DUE_DATE_FIELD_TYPES.has(field.type)),
    [fields],
  );

  const savedDueDateFieldUuid = useMemo(
    () =>
      dueDateFields.find((field) => field.isTaskDueDate)?.value || null,
    [dueDateFields],
  );

  const fieldsMap = useMemo(
    () =>
      new Map(
        fields.map((field) => [
          field.value,
          {
            uuid: field.value,
            label: field.label,
          },
        ]),
      ),
    [fields],
  );

  const systemFields = data?.system_fields?.length
    ? data.system_fields
    : SYSTEM_FIELDS;

  const systemFieldsMap = useMemo(
    () => new Map(systemFields.map((field) => [field.key, field])),
    [systemFields],
  );

  const restoreTemplate = useCallback(() => {
    const template = data?.task_name_template || DEFAULT_TEMPLATE_VALUE;
    setParts(parseTemplate(template, fieldsMap, systemFieldsMap));
    setDueDateFieldUuid(savedDueDateFieldUuid);
  }, [
    data?.task_name_template,
    fieldsMap,
    savedDueDateFieldUuid,
    systemFieldsMap,
  ]);

  useEffect(() => {
    if (!isEdit) {
      restoreTemplate();
    }
  }, [isEdit, restoreTemplate]);

  const closeControls = () => {
    setFieldToAdd(null);
    setSystemFieldToAdd(null);

    setFieldPopoverOpen(false);
    setSystemPopoverOpen(false);
    setFieldModalOpen(false);
    setSystemModalOpen(false);
  };

  const handleStartEdit = () => {
    setIsEdit(true);
  };

  const handleCancelEdit = () => {
    restoreTemplate();
    closeControls();
    setIsEdit(false);
  };

  const updateTextPart = (index, value) => {
    setParts((prev) =>
      prev.map((part, partIndex) =>
        partIndex === index ? { ...part, value } : part,
      ),
    );
  };

  const removePart = (index) => {
    setParts((prev) => {
      const next = prev.filter((_, partIndex) => partIndex !== index);
      return next.length ? next : [createTextPart("")];
    });
  };

  const addTextPart = () => {
    setParts((prev) => [...prev, createTextPart("")]);
  };

  const addFieldPart = () => {
    if (!fieldToAdd) return;

    const field = fields.find((item) => item.value === fieldToAdd);
    if (!field) return;

    setParts((prev) => [
      ...prev,
      {
        type: "field",
        fieldId: field.value,
        label: field.label,
      },
    ]);

    setFieldToAdd(null);
    setFieldPopoverOpen(false);
    setFieldModalOpen(false);
  };

  const addSystemFieldPart = () => {
    if (!systemFieldToAdd) return;

    const field = systemFields.find((item) => item.key === systemFieldToAdd);
    if (!field) return;

    setParts((prev) => [
      ...prev,
      {
        type: "system",
        key: field.key,
        label: field.label,
      },
    ]);

    setSystemFieldToAdd(null);
    setSystemPopoverOpen(false);
    setSystemModalOpen(false);
  };

  const handleSave = async () => {
    const template = serializeTemplate(parts).trim();

    try {
      setPending(true);

      await updateTaskTitleTemplate({
        uuid: data.uuid,
        task_name_template: template || DEFAULT_TEMPLATE_VALUE,
      });

      message.success("Шаблон названия задачи сохранен");
      closeControls();
      setIsEdit(false);

      await fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error?.message || String(error),
        duration: 10,
      });
    } finally {
      setPending(false);
    }
  };

  const handleReset = async () => {
    try {
      setPending(true);

      await updateTaskTitleTemplate({
        uuid: data.uuid,
        task_name_template: DEFAULT_TEMPLATE_VALUE,
      });

      setParts([createTextPart("")]);
      closeControls();
      setIsEdit(false);

      message.success("Шаблон названия задачи сброшен");
      await fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error?.message || String(error),
        duration: 10,
      });
    } finally {
      setPending(false);
    }
  };

  const handleDueDateFieldChange = async (value) => {
    const nextDueDateFieldUuid = value || null;

    if (nextDueDateFieldUuid === savedDueDateFieldUuid) return;

    setDueDateFieldUuid(nextDueDateFieldUuid);

    try {
      setPending(true);

      await updateField({
        uuid: nextDueDateFieldUuid || savedDueDateFieldUuid,
        is_task_due_date: Boolean(nextDueDateFieldUuid),
      });

      message.success("Поле для срока исполнения сохранено");
      await fetchData();
    } catch (error) {
      setDueDateFieldUuid(savedDueDateFieldUuid);
      message.open({
        type: "error",
        content: error?.message || String(error),
        duration: 10,
      });
    } finally {
      setPending(false);
    }
  };

  const readableTemplate = getReadableTemplate(parts).trim();
  const fieldSelectContent = (
    <Space.Compact block className={styles.template_select_block}>
      <Select
        className={styles.template_select}
        showSearch
        allowClear
        placeholder="Выберите поле"
        value={fieldToAdd}
        onChange={setFieldToAdd}
        optionFilterProp="label"
        options={fields.map((field) => ({
          value: field.value,
          label: field.groupName
            ? `${field.groupName} / ${field.label}`
            : field.label,
        }))}
      />

      <Button
        type="primary"
        onClick={addFieldPart}
        className={styles.template_select_button}
      >
        Добавить
      </Button>

      <Button
        type="primary"
        icon={<PlusIcon />}
        onClick={addFieldPart}
        className={styles.template_select_button_mobile}
      />
    </Space.Compact>
  );

  const systemFieldSelectContent = (
    <Space.Compact block className={styles.template_select_block}>
      <Select
        className={styles.template_select}
        allowClear
        placeholder="Выберите системное поле"
        value={systemFieldToAdd}
        onChange={setSystemFieldToAdd}
        options={systemFields.map((field) => ({
          value: field.key,
          label: field.label,
        }))}
      />

      <Button
        type="primary"
        onClick={addSystemFieldPart}
        className={styles.template_select_button}
      >
        Добавить
      </Button>

      <Button
        type="primary"
        icon={<PlusIcon />}
        onClick={addSystemFieldPart}
        className={styles.template_select_button_mobile}
      />
    </Space.Compact>
  );

  const templateActions = canEdit ? (
    !isEdit ? (
      <Button
        type="primary"
        disabled={loading}
        icon={<EditIcon />}
        onClick={handleStartEdit}
      />
    ) : (
      <Flex justify="end" align="center" gap={8} wrap="wrap">
        <Button disabled={loading} onClick={handleCancelEdit}>
          Отмена
        </Button>

        <Button disabled={loading} type="primary" onClick={handleReset}>
          Сбросить
        </Button>

        <Button type="primary" loading={loading} onClick={handleSave}>
          Сохранить
        </Button>
      </Flex>
    )
  ) : null;

  return (
    <Card
      title={
        <Typography.Title level={4} className="no-margin">
          Параметры создаваемых задач
        </Typography.Title>
      }
    >
      {!canEdit ? (
        <Empty description="Параметры можно редактировать только у существующей неудаленной формы" />
      ) : (
        <Flex vertical gap={12}>
          <Card
            size="small"
            title={
              <Typography.Text strong>Шаблон названия задачи</Typography.Text>
            }
            extra={templateActions}
            style={{ boxShadow: "none" }}
          >
            {!isEdit ? (
              <Typography.Text>
                {readableTemplate || DEFAULT_TEMPLATE_TEXT}
              </Typography.Text>
            ) : (
              <Flex vertical gap={6}>
                <Flex
                  align="center"
                  gap={6}
                  wrap="wrap"
                  style={{
                    minHeight: 42,
                  }}
                >
                  {parts.map((part, index) => {
                  if (part.type === "text") {
                    return (
                      <Space.Compact key={index}>
                        <Input
                          disabled={loading}
                          value={part.value}
                          placeholder="Введите текст"
                          onChange={(event) =>
                            updateTextPart(index, event.target.value)
                          }
                          style={{
                            width: Math.max(
                              200,
                              (part.value?.length || 0) * 8 + 40,
                            ),
                            minWidth: 200,
                          }}
                        />

                        {parts.length > 0 && (
                          <Button
                            type="text"
                            style={{
                              border: "1px solid #d9d9d9",
                              borderLeft: "none",
                              borderRadius: "0 4px 4px 0",
                            }}
                            disabled={loading}
                            onClick={() => removePart(index)}
                            icon={<CloseIcon />}
                          />
                        )}
                      </Space.Compact>
                    );
                  }

                  return (
                    <Tag
                      key={index}
                      closable={!loading}
                      closeIcon={<CloseIcon style={{ fontSize: "16px" }} />}
                      onClose={(event) => {
                        event.preventDefault();
                        removePart(index);
                      }}
                      color={part.type === "field" ? "purple" : "blue"}
                      className={styles.template_tag}
                    >
                      <span className={styles.template_tag_text}>
                        {part.label}
                      </span>
                    </Tag>
                  );
                  })}

                  {!isMobile && (
                    <>
                    <Popover
                      trigger="click"
                      placement="bottomRight"
                      open={fieldPopoverOpen}
                      onOpenChange={setFieldPopoverOpen}
                      content={fieldSelectContent}
                    >
                      <span />
                    </Popover>

                    <Popover
                      trigger="click"
                      placement="bottomRight"
                      open={systemPopoverOpen}
                      onOpenChange={setSystemPopoverOpen}
                      content={systemFieldSelectContent}
                    >
                      <span />
                    </Popover>
                    </>
                  )}

                  {isMobile && (
                    <>
                    <Modal
                      title="Добавить поле"
                      open={fieldModalOpen}
                      onCancel={() => setFieldModalOpen(false)}
                      footer={null}
                      destroyOnHidden
                    >
                      {fieldSelectContent}
                    </Modal>

                    <Modal
                      title="Добавить поле"
                      open={systemModalOpen}
                      onCancel={() => setSystemModalOpen(false)}
                      footer={null}
                      destroyOnHidden
                    >
                      {systemFieldSelectContent}
                    </Modal>
                    </>
                  )}

                  <Dropdown
                    trigger={["click"]}
                    menu={{
                      items: [
                        {
                          key: "text",
                          label: "Добавить текст",
                          onClick: addTextPart,
                        },
                        {
                          key: "field",
                          label: "Добавить поле формы",
                          onClick: () => {
                            if (isMobile) {
                              setFieldModalOpen(true);
                            } else {
                              setFieldPopoverOpen(true);
                            }
                          },
                        },
                        {
                          key: "system",
                          label: "Добавить системное поле",
                          onClick: () => {
                            if (isMobile) {
                              setSystemModalOpen(true);
                            } else {
                              setSystemPopoverOpen(true);
                            }
                          },
                        },
                      ],
                    }}
                  >
                    <Button
                      disabled={loading}
                      icon={<PlusIcon />}
                      type="dashed"
                    />
                  </Dropdown>
                </Flex>

                <Flex vertical gap={4}>
                  <Typography.Text type="secondary">
                    Предпросмотр:
                  </Typography.Text>

                  <Typography.Text>
                    {readableTemplate || DEFAULT_TEMPLATE_TEXT}
                  </Typography.Text>
                </Flex>
              </Flex>
            )}
          </Card>

          <Card
            size="small"
            title={
              <Typography.Text strong>Поле для срока исполнения</Typography.Text>
            }
            style={{ boxShadow: "none" }}
          >
            <Flex vertical gap={6}>
              <Select
                showSearch
                allowClear
                loading={loading}
                disabled={loading}
                placeholder="Не выбрано"
                value={dueDateFieldUuid}
                onChange={handleDueDateFieldChange}
                optionFilterProp="label"
                options={dueDateFields.map((field) => ({
                  value: field.value,
                  label: field.groupName
                    ? `${field.groupName} / ${field.label}`
                    : field.label,
                }))}
                style={{ width: "100%", maxWidth: 500 }}
                notFoundContent="Нет полей типа «Дата» или «Момент»"
              />

              <Typography.Text type="secondary">
                Значение выбранного поля будет использовано как срок исполнения
                создаваемой задачи.
              </Typography.Text>
            </Flex>
          </Card>
        </Flex>
      )}
    </Card>
  );
};

export default TaskTitleTemplateCard;
